import { Text } from '@cinch-labs/design-system'
import { datadogRum } from '@datadog/browser-rum'
import cx from 'classnames'
import { Actions, VehicleListing } from '@cinch-nx/data-search'
import { productDetailUrl } from '@cinch-labs/shared-util'
import {
  Card,
  Price,
  ProductImage,
  SpecsList,
  Subtitle,
  Title,
  formatVehicleSpecs,
  parseVehicleDoorTrim,
  useResponsiveEnvironment,
} from '@cinch-nx/search-shared'
import { LocationTitle } from './location-title/location-title'

import styles from './brand-new-vehicle-card.module.css'

type ProductListCardRequiredVehicleListingKeys =
  | 'fuelType'
  | 'transmissionType'
  | 'mileage'
  | 'vehicleYear'
  | 'isReserved'
  | 'make'
  | 'model'
/**
 * VehicleListing, but with most properties made optional
 */
type ProductListCardVehicleListing = Partial<
  Omit<VehicleListing, ProductListCardRequiredVehicleListingKeys>
> &
  Pick<VehicleListing, ProductListCardRequiredVehicleListingKeys> & {
    site?: string
  }

export interface ProductListCardProps extends ProductListCardVehicleListing {
  id: string
  imageStyles?: string
  isPending: boolean
  unavailable?: string
  hasBorder?: boolean
  isHoverable?: boolean
  financeType?: string[]
  showMonthlyPrice: boolean
  onClick?: () => Promise<void>
}

const ERROR_MESSAGE = 'Image Unavailable'

export const BrandNewVehicleCard = ({
  id,
  imageStyles,
  isPending = false,
  unavailable,
  hasBorder = true,
  isHoverable = true,
  showMonthlyPrice,
  onClick,
  financeType = [],
  ...vehicleData
}: ProductListCardProps) => {
  const {
    discountInPence,
    make,
    model,
    modelYear,
    isAvailable,
    isReserved,
    fuelType,
    transmissionType,
    vehicleYear,
    price,
    quoteRegularPaymentInPence,
    quoteType,
    variant,
    thumbnailUrl,
    site,
  } = vehicleData

  const title = `${make} ${model}`
  const subtitle = variant ?? ''

  const { isDesktop } = useResponsiveEnvironment()

  const financeTypeValue = financeType[0]

  const productDetailsLink = productDetailUrl({
    make,
    model,
    id,
    condition: 'new',
    financeType: financeTypeValue,
  })
  const isSold = isAvailable === false

  const tabTarget = isDesktop ? '_blank' : '_self'
  const relValue = isDesktop ? 'opener' : undefined

  const vehiclePrice = price

  const handleOnClick = () => {
    datadogRum.addAction(Actions.VEHICLE_CARD_CLICKED)

    onClick && onClick()
  }

  return (
    <Card
      data-testid="product-list-card"
      element="article"
      hasBorder={hasBorder}
      isHoverable={isHoverable}
      isSearchPage={true}
      isBrandNewVehicle={true}
    >
      <div className={styles['content']}>
        <div
          className={cx(styles['meta'], {
            [styles['collapsed']]: !hasBorder,
            [styles['pending']]: isPending,
          })}
        >
          <div>
            <Title
              element="h3"
              className={styles['title']}
              fontSizeFixed="m"
              type="cardTitle"
              isPending={isPending}
              aria-describedby={
                isReserved ? `vehicle-reserved-${id}` : undefined
              }
            >
              <a
                data-testid="product-list-card-link"
                href={productDetailsLink}
                className={styles['link']}
                onClick={handleOnClick}
                target={tabTarget}
                rel={relValue}
              >
                {title}
                <noscript> {subtitle}</noscript>
              </a>
            </Title>

            <Subtitle
              isPending={isPending}
              fontSize="s"
              className={styles['subtitle']}
            >
              {parseVehicleDoorTrim(subtitle)}
            </Subtitle>

            <SpecsList
              isPending={isPending}
              listStyleClassName={styles['specsList']}
              listItemClassName={styles['specsListItem']}
              isBrandNewVehicle={true}
              items={formatVehicleSpecs({
                fuelType,
                mileage: 0,
                transmissionType,
                modelYear: Number(vehicleYear || modelYear),
              })}
            />
          </div>
          <div>
            {site && <LocationTitle location={site} />}

            {!!vehiclePrice && (
              <Price
                isPending={isPending}
                price={vehiclePrice}
                quoteRegularPaymentInPence={quoteRegularPaymentInPence}
                quoteType={quoteType}
                discountInPence={discountInPence}
                vehicleId={id}
                showAdminFeesButton={false}
                showMonthlyPrice={showMonthlyPrice}
              />
            )}
          </div>
        </div>
      </div>

      <div
        className={cx(styles['imageContainer'], imageStyles, {
          [styles['imageContainerCollapsed']]: !hasBorder,
        })}
      >
        <ProductImage
          alt=""
          isAvailable={isAvailable}
          image={thumbnailUrl}
          unavailableImageMessage={
            (isSold && ' ') || unavailable || ERROR_MESSAGE
          }
          isPending={isPending}
        />

        {isReserved && !isSold && (
          <Text
            fontWeight="book"
            className={`${styles['badge']} ${styles['reserved']}`}
            data-testid="vehicle-is-reserved"
            id={`vehicle-reserved-${id}`}
          >
            Currently reserved
          </Text>
        )}

        {isSold && (
          <Text
            element="span"
            fontSizeFixed="s"
            fontWeight="book"
            className={`${styles['badge']} ${styles['sold']}`}
            data-testid="vehicle-is-sold"
            id={`vehicle-sold-${id}`}
          >
            Sold
          </Text>
        )}
      </div>
    </Card>
  )
}
